/**
 * Deterministic random number generation.
 *
 * Everything about the simulation hangs off this file. A match is reproducible
 * from `(seed, decisionLog)`, which is what lets the Worker replay a submitted
 * result to detect cheating, lets an opponent's innings replay without storing
 * 240 balls, and lets the Daily Challenge hand every player worldwide an
 * identical draft sequence.
 *
 * ## Why xoshiro128** and not PCG32
 *
 * The plan named PCG32, but PCG32 needs 64-bit arithmetic, which in JavaScript
 * means BigInt. BigInt is roughly an order of magnitude slower than 32-bit int
 * ops, and the calibration harness runs ~25 million ball evaluations. xoshiro128**
 * is a 32-bit generator of equivalent statistical quality (it passes PractRand),
 * and it uses only operations JavaScript specifies exactly.
 *
 * ## Cross-platform determinism
 *
 * The generator uses only `Math.imul`, shifts and XOR — all exactly specified by
 * ECMAScript, so browser and Worker produce identical streams. Do not introduce
 * floating-point arithmetic into the state transition; float behaviour is
 * specified too, but it is far easier to get subtly wrong.
 *
 * ## Changing this file invalidates every stored seed
 *
 * A stored match is just a seed. If the number stream changes, every archived
 * match replays differently and verification fails against historical results.
 * `test/rng.test.ts` pins known output vectors to make that impossible to do by
 * accident. If those tests fail, you have changed the meaning of every seed
 * ever issued -- that is a data migration, not a refactor.
 */

/** Mixes a weak seed into well-distributed state. Also used to derive streams. */
function splitmix32(seed: number): () => number {
  let a = seed | 0;
  return () => {
    a = (a + 0x9e3779b9) | 0;
    let t = a ^ (a >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t = t ^ (t >>> 15);
    t = Math.imul(t, 0x735a2d97);
    return (t ^ (t >>> 15)) >>> 0;
  };
}

/** FNV-1a. Turns a string seed into a 32-bit number. */
function hashString(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

export interface Rng {
  /** Raw 32-bit draw. */
  uint32(): number;
  /** Uniform in [0, 1). */
  float(): number;
  /** Uniform integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** Picks an index given non-negative weights. Weights need not sum to 1. */
  weightedIndex(weights: readonly number[]): number;
  /** Picks an element uniformly. */
  pick<T>(items: readonly T[]): T;
  /** True with the given probability. */
  chance(probability: number): boolean;
}

/**
 * Creates a generator. The seed may be a string (match ids, daily challenge
 * dates) or a number.
 */
export function createRng(seed: string | number): Rng {
  const base = typeof seed === 'string' ? hashString(seed) : seed | 0;
  const mix = splitmix32(base);

  // xoshiro128** must not be seeded all-zero. splitmix32 makes that
  // vanishingly unlikely, but the guard costs nothing and the failure mode
  // (a generator stuck returning zero forever) would be maddening to debug.
  let s0 = mix();
  let s1 = mix();
  let s2 = mix();
  let s3 = mix();
  if ((s0 | s1 | s2 | s3) === 0) s0 = 1;

  const uint32 = (): number => {
    const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
    const t = (s1 << 9) >>> 0;
    s2 ^= s0;
    s3 ^= s1;
    s1 ^= s2;
    s0 ^= s3;
    s2 ^= t;
    s3 = rotl(s3, 11);
    s0 >>>= 0;
    s1 >>>= 0;
    s2 >>>= 0;
    s3 >>>= 0;
    return result;
  };

  // 2^-32. Multiplying is faster than dividing and exactly representable.
  const float = (): number => uint32() * 2.3283064365386963e-10;

  const int = (maxExclusive: number): number => {
    if (maxExclusive <= 0) throw new RangeError(`int(${maxExclusive}) needs a positive bound`);
    return Math.floor(float() * maxExclusive);
  };

  const weightedIndex = (weights: readonly number[]): number => {
    let total = 0;
    for (let i = 0; i < weights.length; i++) {
      const w = weights[i];
      if (w < 0 || !Number.isFinite(w)) {
        throw new RangeError(`weight[${i}] must be finite and non-negative, got ${w}`);
      }
      total += w;
    }
    if (total <= 0) throw new RangeError('weights sum to zero -- no outcome is possible');

    let target = float() * total;
    for (let i = 0; i < weights.length; i++) {
      target -= weights[i];
      // Strictly-less avoids returning a zero-weight index when target lands
      // exactly on a boundary.
      if (target < 0) return i;
    }
    // Only reachable through floating-point drift on the final comparison.
    // Fall back to the last index carrying weight rather than returning one
    // that should have been impossible.
    for (let i = weights.length - 1; i >= 0; i--) if (weights[i] > 0) return i;
    throw new Error('unreachable: positive total but no positive weight');
  };

  return {
    uint32,
    float,
    int,
    weightedIndex,
    pick: <T>(items: readonly T[]): T => {
      if (items.length === 0) throw new RangeError('pick() needs a non-empty array');
      return items[int(items.length)];
    },
    chance: (probability: number): boolean => float() < probability,
  };
}

/**
 * Derives an independent, reproducible stream from a parent seed.
 *
 * Used to keep concerns from perturbing one another: commentary phrase choice
 * draws from `derive(matchSeed, 'commentary')`, so adding a new phrase variant
 * cannot change a single ball outcome. Same reasoning for the toss, the CPU's
 * draft, and per-innings player form.
 */
export function deriveSeed(parentSeed: string | number, label: string): string {
  return `${typeof parentSeed === 'string' ? parentSeed : parentSeed | 0}:${label}`;
}
